import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {forkJoin} from 'rxjs';
import {AdxErpPayloadDialogComponent} from '../../../dashboard-finance/adx-erp-panel/components/adx-erp-payload-dialog/adx-erp-payload-dialog.component';
import {ITmtErpSyncTimeline, ITmtErpSyncTimelineEvent} from '../../../dashboard-finance/tmt-erp-panel/interfaces/tmt-erp-panel.interface';
import {TmtErpTimelineService} from '../../../dashboard-finance/tmt-erp-panel/service/tmt-erp-timeline.service';
import {IKtTripChangeLogItem} from '../../interfaces/kt-trip.interface';
import {KtTripChangeLogService} from '../../service/kt-trip-change-log.service';

/** A change-log row enriched with whatever the sync timeline knows about its entity. */
interface IEnrichedChangeLogRow extends IKtTripChangeLogItem {
  matchedEvent: ITmtErpSyncTimelineEvent | null;
}

@Component({
  selector: 'app-kt-trip-history',
  templateUrl: './kt-trip-history.component.html',
  standalone: false,
  styles: [`
    .timeline-summary {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-bottom: 12px;
      font-size: 13px;
    }
    .timeline-table-wrap {
      overflow: auto;
      max-height: 55vh;
    }
    table.timeline {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    table.timeline th, table.timeline td {
      border-bottom: 1px solid #e0e0e0;
      padding: 6px 8px;
      text-align: left;
      vertical-align: top;
    }
    table.timeline th {
      position: sticky;
      top: 0;
      background: #fafafa;
      z-index: 1;
    }
    .error-link {
      color: #d32f2f;
      text-decoration: underline;
      cursor: pointer;
    }
    .status-ok { color: #2e7d32; }
    .status-fail { color: #d32f2f; }
    .status-pending { color: #ed6c02; }
    .status-none { color: #9e9e9e; }
    .hint-note {
      font-size: 12px;
      color: #666;
      font-style: italic;
      margin: 0 0 8px;
    }
  `],
})
export class KtTripHistoryComponent {
  private readonly changeLogService = inject(KtTripChangeLogService);
  private readonly timelineService = inject(TmtErpTimelineService);
  private readonly dialog = inject(MatDialog);

  readonly salesOrderId = input<number | null>(null);

  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly timeline = signal<ITmtErpSyncTimeline | null>(null);
  protected readonly changeLog = signal<IKtTripChangeLogItem[]>([]);

  // Correlated by (EntityType, EntityId) only -- NOT by timestamp. Both ChangeLog.ChangedAtUtc
  // (UTC) and SyncQueue.DateCreated (Eastern, no offset in the raw column) serialize from .NET
  // without an explicit timezone suffix, so comparing them as JS Date objects in the browser
  // would silently assume the wrong zone. Matching on entity identity alone avoids that trap --
  // it answers "has this entity ever been sent, and what's its most recent status", not "was
  // THIS EXACT edit the one in that specific send".
  protected readonly enrichedChangeLog = computed<IEnrichedChangeLogRow[]>(() => {
    const events = this.timeline()?.events ?? [];
    return this.changeLog().map((row) => {
      const matches = events.filter(
        (e) => e.changedEntityType === row.entityType && e.changedEntityId === row.entityId,
      );
      const latest = matches.length
        ? matches.reduce((a, b) => (new Date(a.dateQueued) > new Date(b.dateQueued) ? a : b))
        : null;
      return {...row, matchedEvent: latest};
    });
  });

  constructor() {
    effect(() => {
      const id = this.salesOrderId();
      if (!id) return;
      this.loading.set(true);
      this.error.set(null);
      forkJoin({
        timeline: this.timelineService.getTimeline(id),
        changeLog: this.changeLogService.getChangeLog(id),
      }).subscribe({
        next: ({timeline, changeLog}) => {
          this.timeline.set(timeline);
          // Newest first -- matches the sync-timeline table's own ordering convention.
          this.changeLog.set([...changeLog].sort((a, b) => b.id - a.id));
          this.loading.set(false);
        },
        error: (err: Error) => {
          this.error.set(err.message);
          this.loading.set(false);
        },
      });
    });
  }

  protected statusClass(event: ITmtErpSyncTimelineEvent | null): string {
    if (!event) return 'status-none';
    if (event.isFailed) return 'status-fail';
    if (event.isSynced) return 'status-ok';
    return 'status-pending';
  }

  protected statusLabel(event: ITmtErpSyncTimelineEvent | null): string {
    if (!event) return 'Not sent to ERP';
    if (event.isFailed) return 'Sent to ERP — failed';
    if (event.isSynced) return 'Sent to ERP';
    return 'Waiting to send';
  }

  /** Humanizes a raw C#/SQL field name (e.g. "SubVendorCompanyId") for a non-technical reader. */
  protected humanizeFieldName(fieldName: string): string {
    if (!fieldName) return fieldName;
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\bId\b/g, 'ID');
  }

  /** Friendly entity description, reusing the sync-timeline's own label when this entity has
   * ever been sent to ERP (built server-side with real names/amounts); falls back to the raw
   * type+id only when there's nothing to match against. */
  protected entityDisplay(row: {entityType: string; entityId: number; matchedEvent: ITmtErpSyncTimelineEvent | null}): string {
    const label = row.matchedEvent?.entityLabel;
    return label ? label : `${row.entityType} #${row.entityId}`;
  }

  protected latestResult(event: ITmtErpSyncTimelineEvent): string {
    const last = event.responses?.length ? event.responses[event.responses.length - 1] : null;
    return last?.result ?? '';
  }

  protected latestOrder(event: ITmtErpSyncTimelineEvent): string {
    const last = event.responses?.length ? event.responses[event.responses.length - 1] : null;
    return last?.erpOrderNumber ?? '';
  }

  // ChangedAtUtc is the ONE genuinely-UTC field here -- every other timestamp in this view
  // (dateQueued, erpMessageTimestamp, dateCreated, bookedDate) is already stored as Eastern/
  // Toronto wall-clock time, and displays correctly with a bare `date` pipe because .NET
  // serializes DateTime without a timezone suffix, so the browser parses it as local and the
  // pipe formats it back as local -- a no-op round trip that happens to show the right digits
  // regardless of viewer timezone. ChangedAtUtc is the exception: its naive digits are UTC, not
  // Toronto, so that same no-op round trip would silently mislabel it. Explicitly mark it UTC
  // before formatting, and explicitly request America/Toronto on output, so it's correct
  // regardless of DST.
  protected toTorontoDate(changedAtUtc: string): Date {
    return new Date(changedAtUtc.endsWith('Z') ? changedAtUtc : `${changedAtUtc}Z`);
  }

  protected openError(title: string, content: string): void {
    this.dialog.open(AdxErpPayloadDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {title, content},
    });
  }
}
